/* ==========================================================================
   Cart Rewards - collapse the reward cards while the cart list is scrolled
   --------------------------------------------------------------------------
   The reward cards sit ABOVE the drawer's scrolling item list rather than
   inside it, so they never scroll away. Good for visibility, but on a phone
   the two cards were taking about 240px of a ~660px drawer, leaving very
   little room for the products themselves.

   Once the customer scrolls the item list we mark the stack collapsed and
   assets/cart-rewards.css + assets/cart-tier.css fold each card down - message
   and progress bar only. Scrolling back to the top restores them. The CSS
   applies the collapse on small screens only; a desktop drawer has the room,
   and folding cards away under someone's cursor would just be startling.

   TWO THINGS THIS FILE HAS TO BE CAREFUL ABOUT
   --------------------------------------------------------------------------
   1. Collapsing GIVES the list back height. If the list only just overflowed,
      collapsing can remove the overflow entirely - the browser then clamps
      scrollTop back to 0, we see 0, we un-collapse, and the list springs back
      to the top. To the customer the drawer simply refuses to scroll. So:
      collapse, measure, and if the customer's scroll position no longer fits,
      put it straight back and stop trying for this cart. Measured repro:
      390x660 with 2 products, scrollTop 60 of a possible 138 -> settled at 0.

   2. Below 700px of viewport height the theme moves the scrollbar: the item
      list goes `overflow: visible` and `.cart-drawer__inner` becomes the
      scroller (component-cart-drawer.css). There the cards scroll out of view
      on their own, so collapsing them buys nothing and only risks (1). We sit
      that case out entirely.

   Deliberately standalone and dependency-free:

   - One capture-phase scroll listener on the document. Scroll events don't
     bubble, but they DO capture, so this survives the theme re-rendering the
     drawer wholesale on every cart update - which it does, and which would
     defeat a listener bound directly to the scrolling element.
   - No reliance on CartRewardsCore, so load order doesn't matter.
   - If anything is missing it simply does nothing, and the cards behave
     exactly as they did before.
   ========================================================================== */
(function () {
  'use strict';

  var COLLAPSE_AT = 24; /* px of scroll before the cards fold */
  var EXPAND_AT = 2; /* effectively "back at the top" */
  var SAFETY = 4; /* px of headroom, the cards' padding transition is ~3px */
  var ATTR = 'data-cart-rewards-collapsed';
  var DRAWER_SELECTOR = 'cart-drawer, .cart-drawer, .drawer--cart, #CartDrawer';
  var LIST_SELECTOR = 'cart-drawer-items, .cart-drawer__items, .drawer__cart-items';

  /* Set once the verify step has shown that collapsing this particular cart
     would cost the customer their scroll position. Cleared whenever the cart
     re-renders or the viewport changes, i.e. whenever the sums change. */
  var blocked = false;

  var pending = false;
  var queued = null;

  function maxScroll(el) {
    return el.scrollHeight - el.clientHeight;
  }

  function apply() {
    pending = false;
    var job = queued;
    queued = null;
    if (!job) return;

    var stack = job.stack;
    var scroller = job.scroller;
    if (!stack || !stack.isConnected || !scroller || !scroller.isConnected) return;

    var collapsed = stack.getAttribute(ATTR) === 'true';
    var top = scroller.scrollTop;

    if (!collapsed) {
      if (blocked || top <= COLLAPSE_AT) return;

      /* Collapse, then check the customer can still be where they are. Reading
         scrollHeight forces the layout, so this is measured, not predicted. */
      stack.setAttribute(ATTR, 'true');
      if (maxScroll(scroller) < top + SAFETY) {
        stack.setAttribute(ATTR, 'false');
        scroller.scrollTop = top; /* the read above may have clamped it */
        blocked = true;
      }
      return;
    }

    /* Only unfold once they are genuinely back at the top. Anything looser and
       a clamp reads as "scrolled to the top" and we start flip-flopping. */
    if (top <= EXPAND_AT) stack.setAttribute(ATTR, 'false');
  }

  function schedule(stack, scroller) {
    queued = { stack: stack, scroller: scroller };
    if (pending) return;
    pending = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(apply);
    else setTimeout(apply, 60);
  }

  function onScroll(event) {
    var scroller = event.target;
    if (!scroller || scroller.nodeType !== 1 || !scroller.closest) return;

    /* Only the cart drawer. The page itself scrolling is not our business. */
    var drawer = scroller.closest(DRAWER_SELECTOR);
    if (!drawer) return;

    /* Only when the ITEM LIST is the thing scrolling. On short viewports the
       theme makes the whole drawer panel the scroller and the cards scroll
       away by themselves - see note 2 at the top. */
    if (!scroller.matches(LIST_SELECTOR)) return;

    var stack = drawer.querySelector('.cart-rewards-stack');
    if (!stack) return;

    schedule(stack, scroller);
  }

  /* Capture phase is the whole trick - see the note at the top of the file. */
  try {
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  } catch (e) {
    /* Older browsers without options-object support. */
    document.addEventListener('scroll', onScroll, true);
  }

  function reset() {
    blocked = false;
    var stacks = document.querySelectorAll('.cart-rewards-stack[' + ATTR + "='true']");
    for (var i = 0; i < stacks.length; i++) stacks[i].setAttribute(ATTR, 'false');
  }

  /* A freshly re-rendered drawer starts at scrollTop 0, so clear any stale
     collapsed state rather than leaving the cards folded with nothing
     scrolled - and re-open the question of whether collapsing is worth it,
     because the cart just changed size. Same on resize/rotate. */
  document.addEventListener('shopify:section:load', reset);
  window.addEventListener('resize', reset);
  window.addEventListener('orientationchange', reset);
})();